import Button from '@/components/content/Button'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import { HStack } from '@musinsa/mwms-ds'
import { useCheckedNum } from '@/components/table/GridTable.atoms'
import MConfirm from '@/utils/modal/MConfirm'
import { useRunStandardOrderCutoff } from '@/services/outbound/outboundWaitingStatus/useOutboundWaitingStatusQuries'
import { AgGridReact } from 'ag-grid-react'
import { RefObject, useCallback } from 'react'
import { B2BOrderItem, OutboundWaitingUrlParams } from '@/services/outbound/outboundWaitingStatus/types'
import MToast from '@/utils/modal/MToast'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { useParsedQuery } from '@/hooks/useParsedQuery'

type Props = {
  $grid: RefObject<AgGridReact<B2BOrderItem>>
  onRefetch: () => void
}

export function B2BListTableTitle({ $grid, onRefetch }: Props) {
  const [checkedNum] = useCheckedNum()
  const { parsedQs } = useParsedQuery<OutboundWaitingUrlParams>()

  const { mutateAsync: runStandardOrderCutoff, isPending } = useRunStandardOrderCutoff()

  const isButtonDisabled = checkedNum === 0 || isPending || !$grid.current?.api

  const getSelectedRowsData = useCallback(() => {
    if (!$grid.current?.api) return []
    return $grid.current.api.getSelectedRows() as B2BOrderItem[]
  }, [])

  const handleConfirm = useCallback(
    (checkedNum: number) => {
      const selectedRows = getSelectedRowsData()

      if (selectedRows.length === 0) {
        MToast.error('선택된 출고문서가 없습니다.')
        return
      }

      const outboundDocumentNumbers = selectedRows.map((row) => row.outboundDocumentNumber)

      MConfirm.show(`총 ${checkedNum}건의 출고문서를 확정하시겠습니까?`, {
        subContent: '확정된 출고문서는 출고조회 메뉴에서 확인 할 수 있어요.',
        confirmText: '출고확정',
        closeText: '취소',
        onConfirm: async () => {
          try {
            await runStandardOrderCutoff({
              orderNumbers: outboundDocumentNumbers,
            })

            $grid.current?.api.deselectAll()
            onRefetch()
            MToast.show('출고확정이 완료되었습니다.')
          } catch (e) {
            // 선택 주문 중 한 건이라도 허용 밖 화주면 전체 403 이고 컷오프는 돌지 않는다. 어느 주문인지는 서버가 알려주지 않는다
            MToast.error(
              getShipperScopeErrorMessage(e, '선택한 주문 중 권한이 없는 화주의 주문이 있어 출고확정할 수 없습니다.') ?? '출고확정에 실패했습니다.',
            )
          }
        },
      })
    },
    [runStandardOrderCutoff, getSelectedRowsData],
  )

  return (
    <>
      <HStack gap={16}>
        <HStack gap={2} style={{ flex: '0 0 auto' }}>
          <Text kind="Body_14_bold" color={checkedNum ? colorSystem.text.common.success : colorSystem.text.common.tertiary}>
            {checkedNum.toLocaleString()}
          </Text>
          <Text kind="Body_14_regular">건 선택</Text>
        </HStack>
        {parsedQs.logisticsServiceTypes === 'MFS' && (
          <HStack gap={8} align="center">
            <Button fit disabled={isButtonDisabled} size="sm" kind="secondary" type="button" onClick={() => handleConfirm(checkedNum)}>
              출고확정
            </Button>
          </HStack>
        )}
      </HStack>
    </>
  )
}
